/**
 * Einheitliche Rückmeldung für Aufgaben, deren Prüfung diese Excel-Version
 * nicht hergibt.
 *
 * Excel ist von Office.js gut abgedeckt – die allermeisten Prüfungen des Kurses
 * kommen mit ExcelApi 1.1 bis 1.9 aus. Ein paar Angaben liegen aber höher:
 * Kommentare ab 1.10, Datenschnitte ab 1.10, die Kategorie eines Zahlenformats
 * ab 1.12. Statt in solchen Fällen zu raten oder die Aufgabe still als
 * bestanden zu werten, sagt der Kurs offen, was fehlt und warum.
 *
 * Themen, für die es überhaupt keine Schnittstelle gibt – Sparklines,
 * Szenarien, Gliederung, Makros und andere –, sind kein Fall für
 * `unsupported()`: Es gibt keine Excel-Version, in der die Prüfung über
 * Office.js je funktionieren würde. Sie prüft ein VBA-Prüfmakro (siehe
 * `makros.ts` und `@/office/makro/pruefung`).
 */

import { unsupported } from "@/core/check";
import type { CheckResult } from "@/core/types";

/**
 * @param angabe   Was nicht gelesen werden kann, aus Sicht der lernenden Person.
 * @param abSatz   Ab welchem Anforderungssatz die Angabe zur Verfügung steht.
 * @param selbst   Wie sich die Aufgabe ohne Prüfung selbst kontrollieren lässt.
 * @param mehrzahl `true`, wenn `angabe` mehrere Dinge benennt. Die Meldung
 *                 beugt dann im Plural – ohne das Kennzeichen entstünde
 *                 „Die Filterschaltflächen … lässt sich“.
 */
export function nichtPruefbar(
  angabe: string,
  abSatz: string,
  selbst: string,
  mehrzahl = false,
): CheckResult {
  const satz = mehrzahl
    ? "diese Angaben lassen sich erst ab dem Anforderungssatz"
    : "diese Angabe lässt sich erst ab dem Anforderungssatz";
  return unsupported(
    `${angabe} – ${satz} ExcelApi ${abSatz} auslesen. Diese Excel-Version stellt ` +
      "sie nicht bereit, deshalb kann die Aufgabe hier nicht automatisch geprüft werden.",
    `${selbst} Danach kannst du den Schritt selbst als erledigt markieren.`,
  );
}

/**
 * Rückmeldung, wenn die Arbeitsmappe für eine Aufgabe noch zu leer ist.
 *
 * Eigene Funktion, weil dieser Fall in fast jeder Prüfung vorkommt und
 * ausdrücklich **kein** Fall für `unsupported()` ist: Hier fehlt nicht die
 * Schnittstelle, sondern der Inhalt. Die Aufgabe ist also lösbar.
 */
export const KEIN_INHALT =
  "In der Arbeitsmappe steht noch nichts, was sich prüfen ließe.";
